/*
 * Purpose: Gmail connector fix UI — OAuth, edge inbox, client ID for workflow runs
 * Last modified: 2026-07-10 | Agent
 * Completeness: 100
 */
import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ContextualError } from '@/components/errors/ContextualError';
import { VaultUnlockInline } from '@/components/vault/VaultUnlockInline';
import { REMEDIATION } from '@/lib/remediationRoutes';
import { gmailWebhookPostUrl, isRemoteInboundApi } from '@/lib/apiBase';
import { PRODUCT_BRAND } from '@/lib/productTerms';
import {
  checkWebhookServerHealth,
  createGmailInbox,
  sendTestGmailEvent,
  defaultLinkedInJobFixture,
} from '@/services/connectors/gmailInbound';
import {
  connectGmailOAuth,
  disconnectGmailOAuth,
  getGmailAccessToken,
  hasGoogleOAuthConfig,
} from '@/services/connectors/gmailOAuth';
import { fetchLatestGmailApiMessage } from '@/services/connectors/gmailApi';
import { useSecretStore } from '@/stores/secretStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import type { ConnectorFixIssue } from '@/lib/connectorIssuePolicy';
import { Cloud, Copy, Link2, Mail, RefreshCw, Send, Unlink } from 'lucide-react';
import { toast } from 'sonner';

interface GmailConnectorFixPanelProps {
  issue: ConnectorFixIssue;
  onFixed?: () => void;
}

export function GmailConnectorFixPanel({ issue, onFixed }: GmailConnectorFixPanelProps) {
  const isUnlocked = useSecretStore((s) => s.isUnlocked);
  const setSecret = useSecretStore((s) => s.setSecret);
  const updateNode = useWorkflowStore((s) => s.updateNode);
  const currentWorkflow = useWorkflowStore((s) => s.currentWorkflow);

  const targetNode = currentWorkflow?.nodes.find((n) => n.id === issue.nodeId);
  const nodeInboxId = String(targetNode?.config.inboxId ?? '');
  const nodePostUrl = String(targetNode?.config.postUrl ?? '');
  const nodeSource = String(targetNode?.config.source ?? 'gmail_api');
  const defaultQuery = issue.gmailQuery ?? String(targetNode?.config.gmailQuery ?? 'is:unread newer_than:7d');

  const [serverUp, setServerUp] = useState<boolean | null>(null);
  const [inboxId, setInboxId] = useState(nodeInboxId);
  const [postUrl, setPostUrl] = useState(nodePostUrl);
  const [busy, setBusy] = useState(false);
  const [gmailConnected, setGmailConnected] = useState(Boolean(getGmailAccessToken()));
  const [gmailQuery, setGmailQuery] = useState(defaultQuery);
  const [clientIdDraft, setClientIdDraft] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const refreshHealth = useCallback(async () => {
    setServerUp(await checkWebhookServerHealth());
    setGmailConnected(Boolean(getGmailAccessToken()));
  }, []);

  useEffect(() => {
    void refreshHealth();
  }, [refreshHealth]);

  const patchGmailNode = (patch: Record<string, unknown>) => {
    if (!issue.nodeId || !targetNode) return;
    updateNode(issue.nodeId, {
      config: { ...targetNode.config, ...patch },
    });
    onFixed?.();
  };

  const saveClientId = async () => {
    const val = clientIdDraft.trim();
    if (!val) {
      setLocalError('Paste your Google OAuth Web client ID first.');
      return;
    }
    if (!isUnlocked) {
      setLocalError('Unlock the vault above to save GOOGLE_CLIENT_ID.');
      return;
    }
    setBusy(true);
    setLocalError(null);
    try {
      await setSecret('GOOGLE_CLIENT_ID', val);
      setClientIdDraft('');
      toast.success('GOOGLE_CLIENT_ID saved');
      onFixed?.();
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const onConnectGmail = async () => {
    if (!hasGoogleOAuthConfig()) {
      setLocalError('Add GOOGLE_CLIENT_ID before connecting Gmail.');
      return;
    }
    setBusy(true);
    setLocalError(null);
    try {
      await connectGmailOAuth();
      setGmailConnected(true);
      patchGmailNode({ source: 'gmail_api', useGmailApi: true, gmailQuery });
      toast.success('Gmail connected');
      onFixed?.();
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Gmail OAuth failed');
    } finally {
      setBusy(false);
    }
  };

  const ensureInbox = async () => {
    setBusy(true);
    setLocalError(null);
    try {
      const up = await checkWebhookServerHealth();
      if (!up) {
        setLocalError('Inbound API is offline — run npm run dev or set VITE_API_URL to your production API.');
        return;
      }
      const inbox = await createGmailInbox();
      setInboxId(inbox.inboxId);
      setPostUrl(inbox.postUrl);
      patchGmailNode({
        source: 'webhook',
        inboxId: inbox.inboxId,
        postUrl: inbox.postUrl,
      });
      toast.success('Gmail edge inbox created and wired to your trigger');
      onFixed?.();
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Failed to create inbox');
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    if (!inboxId) {
      setLocalError('Create an inbox first.');
      return;
    }
    setBusy(true);
    setLocalError(null);
    try {
      await sendTestGmailEvent(inboxId, defaultLinkedInJobFixture());
      toast.success('Test email posted — retry your run');
      onFixed?.();
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Test POST failed');
    } finally {
      setBusy(false);
    }
  };

  const onTestGmailApi = async () => {
    setBusy(true);
    setLocalError(null);
    try {
      patchGmailNode({ gmailQuery, source: 'gmail_api', useGmailApi: true });
      const msg = await fetchLatestGmailApiMessage(gmailQuery);
      if (!msg) {
        setLocalError(`No messages matched "${gmailQuery}" — try a broader query or check Gmail.`);
      } else {
        toast.success(`Found: ${msg.subject}`);
        onFixed?.();
      }
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Gmail API test failed');
    } finally {
      setBusy(false);
    }
  };

  const displayPostUrl = postUrl || (inboxId ? gmailWebhookPostUrl(inboxId) : '');

  const copyUrl = () => {
    const path = displayPostUrl.startsWith('http')
      ? displayPostUrl
      : `${window.location.origin}${displayPostUrl.startsWith('/') ? '' : '/'}${displayPostUrl}`;
    void navigator.clipboard.writeText(path);
    toast.success('Webhook URL copied');
  };

  const defaultTab =
    issue.kind === 'gmail_inbox' || nodeSource === 'webhook' ? 'webhook' : 'oauth';

  return (
    <div className="space-y-4">
      {!isUnlocked && (
        <VaultUnlockInline
          onUnlocked={() => {
            setLocalError(null);
            onFixed?.();
          }}
        />
      )}

      {localError && (
        <ContextualError
          message={localError}
          action={{ label: 'Dismiss', onClick: () => setLocalError(null) }}
        />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Mail className="h-4 w-4 text-primary" />
        <Badge variant={gmailConnected ? 'default' : 'secondary'}>
          {gmailConnected ? 'Gmail connected' : 'Gmail not connected'}
        </Badge>
        <Badge variant={serverUp ? 'default' : 'secondary'}>
          {serverUp === null ? 'Checking API…' : serverUp ? 'Inbound API online' : 'Inbound API offline'}
        </Badge>
        {isRemoteInboundApi() && (
          <Badge variant="outline">
            <Cloud className="h-3 w-3 mr-1" />
            Cloud
          </Badge>
        )}
      </div>

      {issue.kind === 'gmail_client_id' && (
        <div className="rounded-lg border border-border p-3 space-y-2">
          <Label className="text-xs">GOOGLE_CLIENT_ID</Label>
          <Input
            value={clientIdDraft}
            onChange={(e) => setClientIdDraft(e.target.value)}
            placeholder="123456789.apps.googleusercontent.com"
            className="text-xs font-mono"
          />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => void saveClientId()} disabled={busy || !isUnlocked}>
              Save to vault
            </Button>
            <Button size="sm" variant="outline" asChild>
              <a href={REMEDIATION.setupGoogle}>Open Setup wizard</a>
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Create a Web OAuth client in Google Cloud Console with Gmail API enabled.
          </p>
        </div>
      )}

      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList className="w-full grid grid-cols-2 h-auto">
          <TabsTrigger value="oauth" className="text-xs">
            Your Gmail (OAuth)
          </TabsTrigger>
          <TabsTrigger value="webhook" className="text-xs">
            Edge inbox (webhook)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="oauth" className="space-y-3 mt-3">
          {!hasGoogleOAuthConfig() && (
            <ContextualError
              message="GOOGLE_CLIENT_ID is missing — add it above or in Setup before connecting."
              action={{ label: 'Open Setup', href: REMEDIATION.setupGoogle }}
              secondaryAction={{ label: 'Builder Secrets', href: REMEDIATION.builderSecrets }}
            />
          )}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => void onConnectGmail()} disabled={busy || !hasGoogleOAuthConfig()}>
              <Link2 className="h-3.5 w-3.5 mr-1" />
              {gmailConnected ? 'Reconnect Gmail' : 'Connect Gmail'}
            </Button>
            {gmailConnected && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  disconnectGmailOAuth();
                  setGmailConnected(false);
                  toast.message('Gmail disconnected');
                }}
              >
                <Unlink className="h-3.5 w-3.5 mr-1" />
                Disconnect
              </Button>
            )}
            <Button size="sm" variant="secondary" onClick={() => void onTestGmailApi()} disabled={!gmailConnected || busy}>
              Test fetch
            </Button>
          </div>
          <div>
            <Label className="text-xs">Gmail search query</Label>
            <Input
              value={gmailQuery}
              onChange={(e) => setGmailQuery(e.target.value)}
              className="text-xs font-mono mt-1"
              placeholder="from:linkedin.com subject:job newer_than:7d"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Uses your signed-in Google account. Token saved as GOOGLE_ACCESS_TOKEN in Secrets (~1h TTL).
          </p>
        </TabsContent>

        <TabsContent value="webhook" className="space-y-3 mt-3">
          <p className="text-xs text-muted-foreground rounded-md border border-border p-2">
            Edge inbox is a synthetic bucket inside {PRODUCT_BRAND} — use OAuth above for real Gmail unless
            another tool POSTs email JSON here.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => void refreshHealth()}>
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Check API
            </Button>
            <Button size="sm" onClick={() => void ensureInbox()} disabled={!serverUp || busy}>
              Create inbox & wire trigger
            </Button>
            <Button size="sm" variant="secondary" onClick={() => void sendTest()} disabled={!inboxId || busy}>
              <Send className="h-3.5 w-3.5 mr-1" />
              Test POST
            </Button>
          </div>
          {inboxId && (
            <div className="space-y-2">
              <div>
                <Label className="text-xs">Inbox ID</Label>
                <Input readOnly value={inboxId} className="text-xs font-mono mt-1" />
              </div>
              <div>
                <Label className="text-xs">POST URL</Label>
                <div className="flex gap-2 mt-1">
                  <Input readOnly value={displayPostUrl} className="text-xs font-mono" />
                  <Button size="icon" variant="outline" onClick={copyUrl} aria-label="Copy URL">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default GmailConnectorFixPanel;
